import MuiModal from '@material-ui/core/Modal'
import moment from 'moment'
import React from 'react'
import styled from 'styled-components'

import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'

import { HIDE_SALES_FORECAST_ADJUSTMENT } from '../constants'
import { getIcon } from '../utils/getIcon'
import {
  IPrepItemBase,
  calculatePrepItemValues,
  formatCurrency,
  formatNonPrepItemValue,
  formatPrepItemValue,
  getNonPrepBackgroundColor,
  getNonPrepTextColor,
  getOnHandUnitLabel,
  getPrepBackgroundColor,
  getPrepTextColor,
  isBucketItem,
} from '../utils/prepItemCalculations'
import { printPrepGuide } from '../utils/printPDF'

const ModalWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  width: 90%;
  min-width: 550px;
  max-width: 700px;
  max-height: 90vh;
  overflow: hidden;
  outline: none;

  @media ${MOBILE_WIDTH} {
    width: 95%;
    max-height: 95vh;
  }
`

const ModalContent = styled.div`
  padding: 30px;
  max-height: calc(90vh - 60px);
  overflow-y: auto;

  @media ${MOBILE_WIDTH} {
    padding: 20px;
    max-height: calc(95vh - 40px);
  }
`

const ModalHeader = styled.div`
  text-align: center;
  margin-bottom: 30px;
`

const Title = styled.h2`
  font-family: Lexend-SemiBold;
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};
  margin: 0 0 10px 0;
`

const SubTitle = styled.div`
  font-size: 16px;
  color: ${COLORS.Steel};
  margin-bottom: 5px;
`

const DateText = styled.div`
  font-size: 14px;
  color: ${COLORS.Steel};
`

const Section = styled.div`
  margin-bottom: 30px;
`

const SectionTitle = styled.h3`
  font-family: Lexend-SemiBold;
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};
  margin: 0 0 15px 0;
`

const ForecastRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
`

const Label = styled.span`
  font-size: 14px;
  color: ${COLORS.Chalkboard};
`

const Value = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) => props.color || COLORS.Chalkboard};
`

const ItemCard = styled.div`
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
`

const ItemHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
`

const ItemIconContainer = styled.div`
  width: 40px;
  height: 40px;
  background-color: #f0f0f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  border: 1px solid #e0e0e0;
`

const ItemInfo = styled.div`
  flex: 1;
`

const ItemName = styled.h4`
  margin: 0;
  font-family: Lexend-SemiBold;
  font-size: 16px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};
`

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 15px;
  font-size: 14px;

  @media ${MOBILE_WIDTH} {
    grid-template-columns: repeat(2, 1fr);
  }
`

const DetailItem = styled.div`
  text-align: center;
`

const DetailLabel = styled.div`
  color: ${COLORS.Steel};
  font-size: 12px;
  margin-bottom: 5px;
`

const DetailValue = styled.div`
  font-weight: 600;
  color: ${COLORS.Chalkboard};
`

const PrepValue = styled.div<{
  backgroundColor?: string
  textColor?: string
}>`
  font-size: 24px;
  font-weight: 700;
  color: ${(props) => props.textColor || COLORS.Basil};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  padding: 4px 8px;
  border-radius: 4px;
  display: inline-block;
`

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  justify-content: center;
  margin-top: 30px;

  @media ${MOBILE_WIDTH} {
    flex-direction: column;
  }
`

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 30px;
  border-radius: 8px;
  font-family: Lexend-SemiBold;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  ${(props) =>
    props.variant === 'primary' &&
    `
    background-color: ${COLORS.Plum};
    color: white;
    border: none;

    &:hover {
      background-color: #3a7bc8;
    }
  `}

  ${(props) =>
    props.variant === 'secondary' &&
    `
    background-color: white;
    color: ${COLORS.Chalkboard};
    border: 2px solid #e0e0e0;

    &:hover {
      background-color: #f5f5f5;
    }
  `}
`

const SuccessMessage = styled.div`
  background-color: ${COLORS.Cucumber};
  border: 1px solid ${COLORS.Basil};
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
  text-align: center;
`

const SuccessTitle = styled.h3`
  margin: 0 0 5px 0;
  font-family: Lexend-SemiBold;
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.Basil};
`

const SuccessText = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${COLORS.Chalkboard};
`

interface IProps {
  isOpen: boolean
  onClose: () => void
  locationName: string
  date: moment.Moment | null
  baseForecast: number
  adjustment: number
  weeklySalesForecast: number
  prepItems: IPrepItemBase[]
  nonPrepItems?: IPrepItemBase[]
  onHandValues: Record<string, Record<string, number>>
  isSubmitted?: boolean
}

const PrepGuideSummaryModal: React.FC<IProps> = ({
  isOpen,
  onClose,
  locationName,
  date,
  baseForecast,
  adjustment,
  weeklySalesForecast,
  prepItems,
  nonPrepItems = [],
  onHandValues,
  isSubmitted = false,
}) => {
  const adjustedForecast = baseForecast * (1 + adjustment / 100)

  const getOnHandValue = (itemName: string, batchType: string) => {
    return onHandValues[itemName]?.[batchType] || 0
  }

  return (
    <MuiModal
      open={isOpen}
      onClose={onClose}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        },
      }}
    >
      <ModalWrapper>
        <ModalContent>
          <ModalHeader>
            <Title>Prep Guide Summary</Title>
            <SubTitle>{locationName}</SubTitle>
            <DateText>{date?.format('dddd, MMMM D, YYYY') || ''}</DateText>
          </ModalHeader>

          <Section>
            <SectionTitle>Sales Forecast</SectionTitle>
            <ForecastRow>
              <Label>Weekly Forecast:</Label>
              <Value>{formatCurrency(weeklySalesForecast)}</Value>
            </ForecastRow>
            <ForecastRow>
              <Label>Base Forecast:</Label>
              <Value>{formatCurrency(baseForecast)}</Value>
            </ForecastRow>
            {!HIDE_SALES_FORECAST_ADJUSTMENT && (
              <ForecastRow>
                <Label>Adjusted Forecast:</Label>
                <Value color={COLORS.Plum}>
                  {formatCurrency(adjustedForecast)}
                </Value>
              </ForecastRow>
            )}
            {!HIDE_SALES_FORECAST_ADJUSTMENT && (
              <ForecastRow>
                <Label>Adjustment:</Label>
                <Value color={COLORS.Plum}>{adjustment}%</Value>
              </ForecastRow>
            )}
          </Section>

          <Section>
            <SectionTitle>OnHand Items</SectionTitle>
            {prepItems.map((item) => {
              const calculations = calculatePrepItemValues(
                item,
                adjustment,
                onHandValues,
              )
              const onHandUnitLabel = getOnHandUnitLabel(item)

              return (
                <ItemCard key={item.prepItemIdCode}>
                  <ItemHeader>
                    <ItemIconContainer>
                      {getIcon(item.prepItemIdCode, 28)}
                    </ItemIconContainer>
                    <ItemInfo>
                      <ItemName>{item.prepItemName}</ItemName>
                    </ItemInfo>
                  </ItemHeader>

                  <DetailGrid>
                    {(() => {
                      const details = item.prepOnHandDetails || []
                      const columns = []

                      // Always show exactly 3 columns
                      for (let i = 0; i < 3; i++) {
                        const detail = details[i]
                        if (detail) {
                          columns.push(
                            <DetailItem key={detail.prepBatchType}>
                              <DetailLabel>{detail.prepBatchType}:</DetailLabel>
                              <DetailValue>
                                {getOnHandValue(
                                  item.prepItemName,
                                  detail.prepBatchType,
                                )}
                              </DetailValue>
                            </DetailItem>,
                          )
                        } else {
                          // Blank column
                          columns.push(
                            <DetailItem key={`blank-${i}`}>
                              <DetailLabel>&nbsp;</DetailLabel>
                              <DetailValue>-</DetailValue>
                            </DetailItem>,
                          )
                        }
                      }
                      return columns
                    })()}

                    <DetailItem>
                      <DetailLabel>On Hand ({onHandUnitLabel}):</DetailLabel>
                      <DetailValue>
                        {calculations.displayOnHand.toFixed(2)}
                      </DetailValue>
                    </DetailItem>

                    {(() => {
                      const isCCItem = isBucketItem(item.prepItemIdCode)
                      return (
                        <>
                          <DetailItem>
                            <DetailLabel>
                              {isCCItem
                                ? 'Needed (Bucket):'
                                : `Needed (${item.prepBatchType}):`}
                            </DetailLabel>
                            <DetailValue>
                              {calculations.needed.toFixed(1)}
                            </DetailValue>
                          </DetailItem>

                          <DetailItem>
                            <DetailLabel>
                              {isCCItem
                                ? 'Prep (Bucket):'
                                : `Prep (${item.prepBatchType}):`}
                            </DetailLabel>
                            <PrepValue
                              backgroundColor={getPrepBackgroundColor(
                                calculations.prep,
                              )}
                              textColor={getPrepTextColor(calculations.prep)}
                            >
                              {formatPrepItemValue(calculations.prep)}
                            </PrepValue>
                          </DetailItem>
                        </>
                      )
                    })()}
                  </DetailGrid>
                </ItemCard>
              )
            })}
          </Section>

          {nonPrepItems.length > 0 && (
            <Section>
              <SectionTitle>Non-Prep Items (Not Scheduled Today)</SectionTitle>
              {nonPrepItems.map((item) => {
                const calculations = calculatePrepItemValues(
                  item,
                  adjustment,
                  onHandValues,
                )
                const onHandUnitLabel = getOnHandUnitLabel(item)

                return (
                  <ItemCard
                    key={item.prepItemIdCode}
                    style={{ backgroundColor: '#fafafa' }}
                  >
                    <ItemHeader>
                      <ItemIconContainer>
                        {getIcon(item.prepItemIdCode, 28)}
                      </ItemIconContainer>
                      <ItemInfo>
                        <ItemName>{item.prepItemName}</ItemName>
                      </ItemInfo>
                    </ItemHeader>

                    <DetailGrid>
                      {(() => {
                        const details = item.prepOnHandDetails || []
                        const columns = []

                        // Always show exactly 3 columns
                        for (let i = 0; i < 3; i++) {
                          const detail = details[i]
                          if (detail) {
                            columns.push(
                              <DetailItem key={detail.prepBatchType}>
                                <DetailLabel>
                                  {detail.prepBatchType}:
                                </DetailLabel>
                                <DetailValue>
                                  {getOnHandValue(
                                    item.prepItemName,
                                    detail.prepBatchType,
                                  )}
                                </DetailValue>
                              </DetailItem>,
                            )
                          } else {
                            // Blank column
                            columns.push(
                              <DetailItem key={`blank-${i}`}>
                                <DetailLabel>&nbsp;</DetailLabel>
                                <DetailValue>-</DetailValue>
                              </DetailItem>,
                            )
                          }
                        }
                        return columns
                      })()}

                      <DetailItem>
                        <DetailLabel>On Hand ({onHandUnitLabel}):</DetailLabel>
                        <DetailValue>
                          {calculations.displayOnHand.toFixed(2)}
                        </DetailValue>
                      </DetailItem>

                      {(() => {
                        const isCCItem = isBucketItem(item.prepItemIdCode)
                        return (
                          <>
                            <DetailItem>
                              <DetailLabel>
                                {isCCItem
                                  ? 'Needed (Bucket):'
                                  : `Needed (${item.prepBatchType}):`}
                              </DetailLabel>
                              <DetailValue>
                                {calculations.needed.toFixed(1)}
                              </DetailValue>
                            </DetailItem>

                            <DetailItem>
                              <DetailLabel>
                                {isCCItem
                                  ? 'Prep (Bucket):'
                                  : `Prep (${item.prepBatchType}):`}
                              </DetailLabel>
                              <PrepValue
                                backgroundColor={getNonPrepBackgroundColor(
                                  calculations.prep,
                                )}
                                textColor={getNonPrepTextColor(
                                  calculations.prep,
                                )}
                              >
                                {formatNonPrepItemValue(calculations.prep)}
                              </PrepValue>
                            </DetailItem>
                          </>
                        )
                      })()}
                    </DetailGrid>
                  </ItemCard>
                )
              })}
            </Section>
          )}

          {isSubmitted && (
            <SuccessMessage>
              <SuccessTitle>Prep Sheet Submitted</SuccessTitle>
              <SuccessText>
                Your daily prep has been successfully submitted.
              </SuccessText>
            </SuccessMessage>
          )}

          <ButtonContainer>
            <Button
              variant="primary"
              onClick={() =>
                printPrepGuide({
                  locationName,
                  date,
                  weeklySalesForecast,
                  baseForecast,
                  adjustment,
                  prepItems,
                  nonPrepItems,
                  onHandValues,
                })
              }
            >
              <span>📄</span> Print to PDF
            </Button>
            <Button variant="secondary" onClick={onClose}>
              <span>✕</span> Close
            </Button>
          </ButtonContainer>
        </ModalContent>
      </ModalWrapper>
    </MuiModal>
  )
}

export default PrepGuideSummaryModal
